import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { FiSearch } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader2 } from "lucide-react";

import { RiDeleteBin6Line } from "react-icons/ri";
import { LuFileText } from "react-icons/lu";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import "./Product.css";
import { Context } from "../../../../utils/context";
import { useFieldArray, useForm } from "react-hook-form";
import { getData } from "../../../../utils/api";
import Summary from "../Summary/Summary";
import LensModal from "../LensModal/LensModal";
import Lense_prescription_modal from "../LensModal/prescription_modal";
// Reuse the same suggestion-list UI used by SearchBar
import SuggestionList from "./SuggestionList";
import { getBarcodeSearchSuggestions } from "../../../../utils/apis/product/searchApi";

const DEBOUNCE_MS = 300;

// Contact Lens category id(s) in your DB (p_category_id)
const CONTACT_LENS_CATEGORY_IDS = [3];

const MIN_QTY = 1;
const MAX_QTY = 99;

function Product({ user_id, tax_type_id, gst_number }) {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const { toggleSidebarFalse, IMG_URL } = useContext(Context);
  const [lensModalShow, setLensModalShow] = useState(false);
  const [lensIndex, setLensIndex] = useState("");
  const [PrescriptionModel, setPrescriptionModel] = useState(false);
  const [prescription, setPrescription] = useState(false);
  const [productId, setProductId] = useState(null);
  const [orderSummaryData, setOrderSummaryData] = useState({});
  const [orderPlaced, setOrderPlaced] = useState(false);

  const { control, register, handleSubmit, watch, setValue, getValues } =
    useForm({
      defaultValues: {
        products: Array.from({ length: 4 }, () => ({
          search: "",
          product_details: "",
          external_order: 0,
          quantity: 1,
        })),
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "products",
  });

  const onSubmit = (data) => {
    console.log("Product Search Input:", data);
  };

  const handleAddRow = () => {
    if (fields.length >= 10) return;
    append({ search: "", product_details: "", external_order: 0, quantity: 1 });
  };

  const handleDeleteRow = (index) => {
    if (fields.length <= 1) return;
    remove(index);
    // clean up any per-row suggestion state for this index
    setSuggestionsMap((m) => {
      const next = { ...m };
      delete next[index];
      return next;
    });
    // decimal inpiut
    setRawInputMap((m) => {
      const next = { ...m };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${index}_`)) delete next[key];
      });
      return next;
    });
  };

  // ---- Suggestion-search state (per row, keyed by field index) ----
  const [suggestionsMap, setSuggestionsMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [openMap, setOpenMap] = useState({});
  const [highlightMap, setHighlightMap] = useState({});

  const [rawInputMap, setRawInputMap] = useState({});

  const debounceRefs = useRef({});
  const requestIdRefs = useRef({});
  const searchCellRefs = useRef({});

  useEffect(() => {
    const onClickOutside = (e) => {
      Object.entries(searchCellRefs.current).forEach(([idx, el]) => {
        if (el && !el.contains(e.target)) {
          setOpenMap((m) => (m[idx] ? { ...m, [idx]: false } : m));
        }
      });
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    (index, kw) => {
      if (!kw || !kw.trim()) {
        setSuggestionsMap((m) => ({ ...m, [index]: [] }));
        setLoadingMap((m) => ({ ...m, [index]: false }));
        return;
      }

      const categoryId = watch(`products.${index}.p_category_id`) || "";
      const reqId = (requestIdRefs.current[index] || 0) + 1;
      requestIdRefs.current[index] = reqId;
      setLoadingMap((m) => ({ ...m, [index]: true }));

      getBarcodeSearchSuggestions(categoryId, kw)
        .then((list) => {
          if (requestIdRefs.current[index] !== reqId) return;
          setSuggestionsMap((m) => ({
            ...m,
            [index]: Array.isArray(list) ? list : [],
          }));
          setLoadingMap((m) => ({ ...m, [index]: false }));
          setHighlightMap((m) => ({ ...m, [index]: -1 }));
        })
        .catch(() => {
          if (requestIdRefs.current[index] !== reqId) return;
          setSuggestionsMap((m) => ({ ...m, [index]: [] }));
          setLoadingMap((m) => ({ ...m, [index]: false }));
        });
    },
    [watch],
  );

  const handleSearchChange = (index, val) => {
    setValue(`products.${index}.search`, val);
    setOpenMap((m) => ({ ...m, [index]: true }));

    if (debounceRefs.current[index]) clearTimeout(debounceRefs.current[index]);
    debounceRefs.current[index] = setTimeout(() => {
      fetchSuggestions(index, val);
    }, DEBOUNCE_MS);
  };

  // Fetches full product details (mrp, discount, hsn_code, p_category_id, etc.)
  // by product id and stores the whole response object as product_details.
  const fetchProductDetails = async (index, id) => {
    setLoadingMap((m) => ({ ...m, [index]: true }));
    try {
      const res = await getData(`/website/product/${id}`);
      if (res?.success && res.data) {
        setValue(`products.${index}.product_details`, res.data);
      }
    } catch (err) {
      console.error("Error fetching product details:", err);
    } finally {
      setLoadingMap((m) => ({ ...m, [index]: false }));
    }
  };

  const handleSelectSuggestion = (index, item) => {
    setValue(`products.${index}.search`, item.description || "");
    // a newly selected product always starts with quantity 1
    setValue(`products.${index}.quantity`, 1);
    setOpenMap((m) => ({ ...m, [index]: false }));
    fetchProductDetails(index, item.id);
  };

  const handleSearchKeyDown = (e, index) => {
    const suggestions = suggestionsMap[index] || [];
    if (!openMap[index] || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightMap((m) => ({
        ...m,
        [index]: Math.min((m[index] ?? -1) + 1, suggestions.length - 1),
      }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightMap((m) => ({
        ...m,
        [index]: Math.max((m[index] ?? -1) - 1, 0),
      }));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hi = highlightMap[index] ?? -1;
      if (hi >= 0 && suggestions[hi])
        handleSelectSuggestion(index, suggestions[hi]);
    } else if (e.key === "Escape") {
      setOpenMap((m) => ({ ...m, [index]: false }));
    }
  };

  const recalcProductRow = (index, changedField, rawValue) => {
    const details = getValues(`products.${index}.product_details`) || {};

    let mrp = Number(details.mrp) || 0;
    let discount = Number(details.discount) || 0;
    let discount_amount = Number(details.discount_amount) || 0;
    let tax_percentage = Number(details.tax_percentage) || 0;
    let tax_amount = Number(details.tax_amount) || 0;

    const numericValue = rawValue === "" ? 0 : Number(rawValue);

    // Only MRP and Discount % are user-editable now.

    if (changedField === "mrp") {
      mrp = numericValue;
      discount_amount = (mrp * discount) / 100;
    } else if (changedField === "discount") {
      discount = numericValue;
      discount_amount = (mrp * discount) / 100;
    } else if (changedField === "discount_amount") {
      discount_amount = numericValue;
      discount = mrp > 0 ? (discount_amount / mrp) * 100 : 0;
    } else if (changedField === "tax_percentage") {
      tax_percentage = numericValue;
    }

    discount_amount = Math.max(0, Math.min(discount_amount, mrp));
    discount = mrp > 0 ? (discount_amount / mrp) * 100 : 0;

    const price = Math.max(mrp - discount_amount, 0);
    tax_amount = (price * tax_percentage) / 100;
    const base_amount = Math.max(price - tax_amount, 0);

    setValue(
      `products.${index}.product_details.mrp`,
      changedField === "mrp" ? rawValue : Math.round(mrp),
    );
    setValue(
      `products.${index}.product_details.discount`,
      changedField === "discount" ? rawValue : discount.toFixed(2),
    );
    setValue(
      `products.${index}.product_details.discount_amount`,
      changedField === "discount_amount"
        ? rawValue
        : Math.round(discount_amount),
    );
    setValue(
      `products.${index}.product_details.tax_percentage`,
      changedField === "tax_percentage" ? rawValue : tax_percentage.toFixed(2),
    );
    setValue(
      `products.${index}.product_details.tax_amount`,
      tax_amount.toFixed(2),
    );
    setValue(
      `products.${index}.product_details.base_amount`,
      base_amount.toFixed(2),
    );
    setValue(`products.${index}.product_details.price`, price.toFixed(2));
  };

  const handleProductFieldChange = (index, field, value) => {
    recalcProductRow(index, field, value);
  };

  const handleNumericInputChange = (index, field, value) => {
    setRawInputMap((m) => ({ ...m, [`${index}_${field}`]: value }));
    handleProductFieldChange(index, field, value);
  };

  const handleNumericInputBlur = (index, field) => {
    setRawInputMap((m) => {
      const next = { ...m };
      delete next[`${index}_${field}`];
      return next;
    });
  };

  // ---- Quantity (contact lenses only) ----
  const clampQty = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return MIN_QTY;
    return Math.min(Math.max(n, MIN_QTY), MAX_QTY);
  };

  const setQuantity = (index, val) => {
    setValue(`products.${index}.quantity`, clampQty(val));
  };

  const changeQuantityBy = (index, delta) => {
    const current = clampQty(getValues(`products.${index}.quantity`));
    setQuantity(index, current + delta);
  };

  const handleAddLens = async (index) => {
    if (!user_id) {
      alert("Please Add User Details...!");
      return false;
    }
    const details = watch(`products.${index}.product_details`);
    setProductId(details?.id || null);
    await setLensModalShow(true);
    await setLensIndex(index);
  };

  const handleprescription = (index) => {
    if (!user_id) {
      alert("Please Add User Details...!");
      return;
    }
    const details = watch(`products.${index}.product_details`);
    setProductId(details?.id || null);
    setLensIndex(index);
    setPrescriptionModel(true);
  };

  useEffect(() => {
    console.log("Order Summary Data:", orderSummaryData);
  }, [orderSummaryData]);

  const handleRemoveProduct = (index) => {
    setValue(`products.${index}.product_details`, null);
    setValue(`products.${index}.search`, "");
    setValue(`products.${index}.quantity`, 1);
  };

  const handleRemoveLens = (index) => {
    setValue(`products.${index}.product_details.lens_details`, null);
  };
  const handleRemoveAdd = (index) => {
    setValue(`products.${index}.product_details.addon_details`, null);
  };

  // Sends 1 when checked, 0 when unchecked
  const handleExternalOrderChange = (index, checked) => {
    setValue(`products.${index}.external_order`, checked ? 1 : 0);
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <section className="product-container">
            <h4 className="product-title text-center">Products</h4>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <div className="invoice-header">
                <div>S.No</div>
                <div>HSN</div>
                <div>Product Details</div>
                <div>MRP</div>
                <div>Dis %</div>
                <div>Dis Amt</div>

                <div>Tax %</div>
                <div>Tax Amt</div>

                <div>Final Price</div>
                <div>Qty</div>

                <div>Add Supplier</div>
                <div>Rx</div>
                <div></div>
              </div>

              {fields.map((item, index) => {
                const details = watch(`products.${index}.product_details`);
                // Lens/prescription icon only shows for these category ids

                const isLens =
                  details?.p_category_id === 1 ||
                  details?.p_category_id === 3 ||
                  details?.p_category_id === 10;

                // Quantity stepper only for contact lens products
                const isContactLens = CONTACT_LENS_CATEGORY_IDS.includes(
                  Number(details?.p_category_id),
                );
                const qtyVal = clampQty(watch(`products.${index}.quantity`));

                const searchVal = watch(`products.${index}.search`) || "";
                const showPopup =
                  !orderPlaced && openMap[index] && searchVal.trim().length > 0;
                const externalOrderVal = watch(
                  `products.${index}.external_order`,
                );

                const mrpKey = `${index}_mrp`;
                const discountKey = `${index}_discount`;
                const discountAmountKey = `${index}_discount_amount`;
                const taxPercentageKey = `${index}_tax_percentage`;

                const mrpDisplay =
                  rawInputMap[mrpKey] !== undefined
                    ? rawInputMap[mrpKey]
                    : details?.mrp
                      ? Math.round(details.mrp)
                      : "";

                const discountDisplay =
                  rawInputMap[discountKey] !== undefined
                    ? rawInputMap[discountKey]
                    : details?.discount !== undefined &&
                        details?.discount !== null
                      ? Number(details.discount).toFixed(2)
                      : "";

                const discountAmountDisplay =
                  rawInputMap[discountAmountKey] !== undefined
                    ? rawInputMap[discountAmountKey]
                    : details?.discount_amount
                      ? Math.round(details.discount_amount)
                      : "";

                const taxPercentageDisplay =
                  rawInputMap[taxPercentageKey] !== undefined
                    ? rawInputMap[taxPercentageKey]
                    : details?.tax_percentage !== undefined &&
                        details?.tax_percentage !== null
                      ? Number(details.tax_percentage).toFixed(2)
                      : "";

                return (
                  <div
                    className="invoice-row"
                    key={item.id}
                    onMouseEnter={() => setHoveredProduct(details)}
                  >
                    <div>{index + 1}</div>
                    <div>{details?.hsn_code || "-"}</div>

                    <div>
                      <div
                        className="product-search-cell"
                        style={{ position: "relative" }}
                        ref={(el) => (searchCellRefs.current[index] = el)}
                      >
                        <div className="product-search-input-wrap">
                          <FiSearch className="search-icon" />
                          <input
                            type="text"
                            className="product-search-input"
                            placeholder="Select Product"
                            value={searchVal}
                            disabled={orderPlaced}
                            onChange={(e) =>
                              handleSearchChange(index, e.target.value)
                            }
                            onFocus={() => {
                              if (searchVal.trim()) {
                                setOpenMap((m) => ({ ...m, [index]: true }));
                              }
                            }}
                            onKeyDown={(e) => handleSearchKeyDown(e, index)}
                          />
                          {loadingMap[index] && (
                            <Loader2
                              size={14}
                              className="product-search-spin"
                            />
                          )}
                        </div>

                        {showPopup && (
                          <div className="product-search-popup">
                            <SuggestionList
                              suggestions={suggestionsMap[index] || []}
                              loading={!!loadingMap[index]}
                              keyword={searchVal}
                              highlightedIndex={highlightMap[index] ?? -1}
                              onHover={(i) =>
                                setHighlightMap((m) => ({ ...m, [index]: i }))
                              }
                              onSelect={(sel) =>
                                handleSelectSuggestion(index, sel)
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Editable pricing */}
                    {/* MRP — editable */}
                    <div className="invoice-input-wrap">
                      <input
                        type="number"
                        step="1"
                        className="invoice-number-input"
                        value={mrpDisplay}
                        disabled={orderPlaced}
                        onChange={(e) =>
                          handleNumericInputChange(index, "mrp", e.target.value)
                        }
                        onBlur={() => handleNumericInputBlur(index, "mrp")}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {/* Discount %  */}
                    <div className="invoice-input-wrap">
                      <input
                        type="number"
                        step="0.01"
                        className="invoice-number-input"
                        value={discountDisplay}
                        disabled={orderPlaced}
                        onChange={(e) =>
                          handleNumericInputChange(
                            index,
                            "discount",
                            e.target.value,
                          )
                        }
                        onBlur={() => handleNumericInputBlur(index, "discount")}
                        onWheel={(e) => e.target.blur()}
                      />
                      <span className="input-suffix">%</span>
                    </div>

                    {/* Discount Amt  */}
                    <div className="invoice-input-wrap">
                      <span className="input-prefix">₹</span>
                      <input
                        type="number"
                        step="1"
                        className="invoice-number-input"
                        value={discountAmountDisplay}
                        disabled={orderPlaced}
                        onChange={(e) =>
                          handleNumericInputChange(
                            index,
                            "discount_amount",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          handleNumericInputBlur(index, "discount_amount")
                        }
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {/* Tax % */}
                    <div className="invoice-input-wrap">
                      <input
                        type="number"
                        step="0.01"
                        className="invoice-number-input"
                        value={taxPercentageDisplay}
                        disabled={orderPlaced}
                        onChange={(e) =>
                          handleNumericInputChange(
                            index,
                            "tax_percentage",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          handleNumericInputBlur(index, "tax_percentage")
                        }
                        onWheel={(e) => e.target.blur()}
                      />
                      <span className="input-suffix">%</span>
                    </div>

                    {/* Tax Amount */}
                    <div>
                      {details?.tax_amount
                        ? `₹${Number(details.tax_amount).toFixed(2)}`
                        : ""}
                    </div>

                    {/* Final Price = price (derived: mrp - discount_amount), read-only */}
                    <div className="final-price-cell">
                      {details?.price
                        ? `₹${Number(details.price).toFixed(2)}`
                        : ""}
                    </div>

                    {/* Qty — stepper only for Contact Lens products */}
                    <div className="qty-cell">
                      {isContactLens && (
                        <div className="qty-stepper">
                          <button
                            type="button"
                            className="qty-btn"
                            disabled={orderPlaced || qtyVal <= MIN_QTY}
                            onClick={() => changeQuantityBy(index, -1)}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>

                          <input
                            type="number"
                            className="qty-input"
                            min={MIN_QTY}
                            max={MAX_QTY}
                            value={qtyVal}
                            disabled={orderPlaced}
                            onChange={(e) => setQuantity(index, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />

                          <button
                            type="button"
                            className="qty-btn"
                            disabled={orderPlaced || qtyVal >= MAX_QTY}
                            onClick={() => changeQuantityBy(index, 1)}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="add-supplier">
                      <Form.Check
                        type="checkbox"
                        checked={!!externalOrderVal}
                        disabled={orderPlaced}
                        onChange={(e) =>
                          handleExternalOrderChange(index, e.target.checked)
                        }
                      />
                    </div>

                    <div className="prescription">
                      {isLens && (
                        <LuFileText
                          className="action-icon prescription-icon"
                          onClick={() =>
                            !orderPlaced && handleprescription(index)
                          }
                          style={{
                            opacity: orderPlaced ? 0.4 : 1,
                            pointerEvents: orderPlaced ? "none" : "auto",
                          }}
                        />
                      )}
                    </div>

                    <div className="delete">
                      {fields.length > 1 && (
                        <RiDeleteBin6Line
                          className="action-icon delete-icon"
                          onClick={() => !orderPlaced && handleDeleteRow(index)}
                          style={{
                            opacity: orderPlaced ? 0.4 : 1,
                            pointerEvents: orderPlaced ? "none" : "auto",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              {fields.length < 10 && !orderPlaced && (
                <div className="add-btn-floating">
                  <Button variant="dark" onClick={handleAddRow}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Product
                  </Button>
                </div>
              )}
            </Form>
          </section>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <Summary
            prescription={prescription}
            control={control}
            user_id={user_id}
            getValues={getValues}
            watch={watch}
            setValue={setValue}
            setOrderSummaryData={setOrderSummaryData}
            hoveredProduct={hoveredProduct}
            tax_type_id={tax_type_id}
            gst_number={gst_number}
            setOrderPlaced={setOrderPlaced}
          />
        </Col>
      </Row>

      <Lense_prescription_modal
        user_id={user_id}
        setPrescription={setPrescription}
        productId={productId}
        handleAddLens={handleAddLens}
        show={PrescriptionModel}
        onHide={() => setPrescriptionModel(false)}
        setValueMain={setValue}
        index={lensIndex}
        getValues={getValues}
        watch={watch}
        register={register}
        setLensIndex={setLensIndex}
      />

      <LensModal
        user_id={user_id}
        setPrescription={setPrescription}
        productId={productId}
        show={lensModalShow}
        onHide={() => setLensModalShow(false)}
        setValue={setValue}
        index={lensIndex}
        getValues={getValues}
        watch={watch}
        register={register}
        setLensIndex={setLensIndex}
      />

      <style>{`
        .product-search-input-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #D5D9D9;
          border-radius: 4px;
          padding: 4px 8px;
          background: #fff;
        }
        .product-search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 13px;
          min-width: 0;
        }
        .product-search-spin {
          color: #9CA3AF;
          animation: product-search-spin 0.8s linear infinite;
        }
        @keyframes product-search-spin { to { transform: rotate(360deg); } }
        .product-search-popup {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          z-index: 70;
        }
        .add-supplier {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* ---- Qty stepper (contact lenses) — sits in its own grid column ---- */
        .qty-cell {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .qty-stepper {
          display: inline-flex;
          align-items: center;
          border: 1px solid #D5D9D9;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
        }
        .qty-btn {
          width: 28px;
          height: 30px;
          border: none;
          background: #F3F4F6;
          font-size: 18px;
          line-height: 1;
          cursor: pointer;
          padding: 0;
        }
        .qty-btn:hover:not(:disabled) { background: #E5E7EB; }
        .qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .qty-input {
          width: 38px;
          height: 30px;
          border: none;
          border-left: 1px solid #D5D9D9;
          border-right: 1px solid #D5D9D9;
          text-align: center;
          outline: none;
          font-size: 13px;
          padding: 0;
          -moz-appearance: textfield;
        }
        .qty-input::-webkit-outer-spin-button,
        .qty-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>
    </>
  );
}

export default Product;